import { inject, Injectable } from '@angular/core';
import { PreferenceModel, UserModel } from '@seahorse/domain';
import { AUTH_ROUTES } from '../tokens/auth-routes.token';
import { StoredAccountInfo } from '../models/stored-account-info.model';

const TOKEN_KEY = 'seahorse-token';
const ACCOUNT_INFO_KEY = 'seahorse-account-info';
// Device preferences that survive a logout (not account data). The theme
// mode (old/new look, @seahorse/theme ThemeModeService) is read before login,
// so clearing it would show the login page in the other look.
const PRESERVED_ON_LOGOUT_KEYS = ['shipm8.theme-mode'];

@Injectable({
  providedIn: 'root',
})
export class IdentityServiceBase {
  identity?: UserModel;
  permissions: string[] = [];
  protected preferences: PreferenceModel[] = [];

  isMfaEnabled = false;

  get token(): string {
    return localStorage.getItem(TOKEN_KEY) || '';
  }

  set token(value: string) {
    value
      ? localStorage.setItem(TOKEN_KEY, value)
      : localStorage.removeItem(TOKEN_KEY);
  }

  get isAuthenticated(): boolean {
    return !!this.token;
  }

  get userPermissions(): string[] {
    return this.permissions;
  }

  private _loginRoute = inject(AUTH_ROUTES).login;

  setIdentity(accessToken: string, reload = false) {
    if (!accessToken) {
      return;
    }

    this.token = accessToken;

    if (reload) {
      window.location.replace('/');
    }
  }

  clearIdentity(redirectToLogin = true) {
    const allAccountsInfo = this.getAllAccountsInfo();
    const currentToken = this.token;

    // Remove current account from stored accounts
    const updatedAccountsInfo = allAccountsInfo.filter(
      (a) => a.token !== currentToken
    );

    const nextActiveToken =
      updatedAccountsInfo.length > 0 ? updatedAccountsInfo[0].token : null;

    if (nextActiveToken) {
      localStorage.setItem(TOKEN_KEY, nextActiveToken);
      localStorage.setItem(
        ACCOUNT_INFO_KEY,
        JSON.stringify(updatedAccountsInfo)
      );

      window.location.replace('/');
    } else {
      const preserved = PRESERVED_ON_LOGOUT_KEYS.map(
        (key) => [key, localStorage.getItem(key)] as const
      );
      localStorage.clear();
      preserved.forEach(
        ([key, value]) => value !== null && localStorage.setItem(key, value)
      );
      window.sessionStorage.clear();

      if (redirectToLogin) {
        window.location.replace(this._loginRoute);
      }
    }
  }

  getAllAccountsInfo(): StoredAccountInfo[] {
    const accountsJson = localStorage.getItem(ACCOUNT_INFO_KEY);
    return accountsJson ? JSON.parse(accountsJson) : [];
  }

  saveAccountInfo() {
    if (!this.identity) {
      return;
    }

    const accountInfo = {
      id: this.identity.id || '',
      fullName: `${this.identity.givenName || ''} ${
        this.identity.surname || ''
      }`.trim(),
      organisationName: this.identity.organisationName || '',
      emailAddress: this.identity.emailAddress || '',
      token: this.token,
    };

    const allAccounts = this.getAllAccountsInfo();

    const filteredAccounts = allAccounts.filter((a) => a.id !== accountInfo.id);
    filteredAccounts.push(accountInfo);

    localStorage.setItem(ACCOUNT_INFO_KEY, JSON.stringify(filteredAccounts));
  }

  removeInvalidAccountInfo(token: string) {
    const allAccounts = this.getAllAccountsInfo();
    const filteredAccounts = allAccounts.filter((a) => a.token !== token);
    localStorage.setItem(ACCOUNT_INFO_KEY, JSON.stringify(filteredAccounts));
  }

  redirectToLoginWithEmail(email: string) {
    const encodedEmail = encodeURIComponent(email);
    window.location.replace(`${this._loginRoute}?email=${encodedEmail}`);
  }

  hasPermission(permission: string) {
    if (permission.indexOf(',') > -1) {
      const permissionList = permission.split(',');
      const common = permissionList.filter((p) => this.permissions.includes(p));
      return common.length > 0;
    } else {
      return this.permissions.indexOf(permission) > -1;
    }
  }

  setPermissions(permissions: any) {
    if (!permissions) {
      return;
    }

    for (const prop in permissions) {
      if (permissions[prop].length === 0) {
        this.permissions.push(prop);
      } else {
        const mapped = permissions[prop].map((p: any) =>
          prop === p ? prop : prop + '.' + p
        );
        Array.prototype.push.apply(this.permissions, mapped);
      }
    }
  }

  hasFeature(feature: string): boolean {
    return true;
  }

  getPreferences(): PreferenceModel[] {
    // always return a copy, use the method 'setPermissions' to update the preferences
    return (this.preferences ?? []).map((p) => ({ ...p }));
  }

  setPreferences(input: PreferenceModel[]) {
    if (!input) {
      return;
    }

    input.forEach((preference) => {
      const index = this.preferences.findIndex((p) => {
        return p['id'] === preference['id'];
      });

      if (index === -1) {
        this.preferences.push(preference);
      } else {
        this.preferences[index].fieldValue = preference.fieldValue;
      }
    });
  }

  updatePreference(preference: PreferenceModel) {
    const index = this.preferences.findIndex((p) => {
      return p['id'] === preference['id'];
    });

    if (index !== -1) {
      this.preferences[index].fieldValue = preference.fieldValue;
    } else {
      this.preferences.push(preference);
    }
  }
}
